/* eslint-disable react-hooks/exhaustive-deps */
import * as React from "react";
import { NavBar } from "../../common/NavBar";
import { ParentGroupsDropDown } from "./ParentGroupsDropDown";
import { SiteDropDown } from "./SiteDropDown";
import { CameraDropDown } from "./CameraDropDown";
import { StartDateDropDown } from "./StartDateDropDown";
import { EndDateDropDown } from "./EndDateDropDown";
import { SVGLine } from "./SVGLine";
import { DownloadButton } from "./DownloadButton";
import Checkbox from "@mui/material/Checkbox";
import { AlertsByDateGraph } from "./AlertsByDateGraph";
import { AlertsByParentGroupsGraph } from "./AlertsByParentGroupsGraph";
import { FalsePositiveReductionGraphAnalytics } from "./FalsePositiveReductionGraphAnalytics";
import { PercentageofMotionFilteredGraph } from "./PercentageofMotionFilteredGraph";
import { AnalyticsAlerts } from "./AnalyticsAlerts";
import { Dispatcher } from "../Sites/SiteAboutPage/SiteAboutPageUtils/siteAboutInterfaces";
import { Link, Tooltip } from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { Button } from "@mui/material";
import {
  fetchAllAnalyticsPageData,
  fetchCamerasBySite,
  fetchCamerasBySiteAndSetDropdownValues,
  fetchSitesByParentGroup,
  fetchSitesByParentGroupAndSetDropdownValues,
  handleGetDownloadURL,
  handleNavigatedFromSitesPage,
  handleSetDefaultAnalyticsFilters,
  useAnalyticsStore,
} from "./AnalyticsStore/analyticsStore";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  formattedDateGenerator,
  updateSearchParamValues,
} from "../../utils/updateSearchParamValues";
import { useAppStore } from "../../../appStore";

declare module "@mui/material/styles" {
  interface Palette {
    neutral: Palette["primary"];
  }
}

const theme = createTheme({
  palette: {
    primary: {
      main: "#FF8400",
    },
  },
});

export const Analytics = ({
  navBarCollapsed,
  setNavBarCollapsed,
}: {
  navBarCollapsed: boolean;
  setNavBarCollapsed: Dispatcher<boolean>;
}) => {
  const navigate = useNavigate();
  const parseNumberList = (str: string | null): number[] => {
    if (!str) return [];
    // Decode URL encoding and split by comma
    const decoded = decodeURIComponent(str);
    return decoded.split(",").map(Number);
  };

  const {
    loadingData,
    userFilters,
    filtersSavedClicked,
    alertURL,
    setDownloadURL,
    setDateGraphName,
    setAlertsGraphName,
    setDateChangesMade,
    fetchAllGraphsData,
    resetState,
    setAlertURL,
    totalAlerts,
    setUserFilters,
    setFiltersApplied,
    filtersApplied,
  } = useAnalyticsStore();

  const [searchParams, setSearchParams] = useSearchParams();

  const group = searchParams.get("group");
  const site = searchParams.get("site");
  const camera = searchParams.get("camera");
  const start_date = searchParams.get("start_date");
  const end_date = searchParams.get("end_date");

  const { setNavigatedToAlertsFromAnalytics } = useAppStore();

  React.useEffect(() => {
    if (!group && !site && !camera && !start_date && !end_date) {
      setFiltersApplied(false);
    } else {
      setFiltersApplied(true);
    }
  }, []);

  React.useEffect(() => {
    setUserFilters({
      parent_group: group || "",
      site: site ? parseNumberList(site).map(String) : [],
      camera: camera ? parseNumberList(camera).map(String) : [],
      start_date: start_date ? new Date(start_date) : new Date(),
      end_date: end_date ? new Date(end_date) : new Date(),
    });

    if (group) {
      if (site) {
        // If we have both group and site, use the function that sets dropdown values
        const siteIds = parseNumberList(site);
        fetchSitesByParentGroupAndSetDropdownValues(group, siteIds, navigate);
      } else {
        // If we only have group, use the regular function
        fetchSitesByParentGroup(group, navigate);
      }
    }

    if (site) {
      if (camera) {
        // If we have both site and camera, use the function that sets dropdown values
        const siteIds = parseNumberList(site).map(String);
        const cameraIds = parseNumberList(camera);
        fetchCamerasBySiteAndSetDropdownValues(siteIds, cameraIds, navigate);
      } else {
        // If we only have site, use the regular function
        fetchCamerasBySite([site], navigate);
      }
    }
  }, []);

  const seeAlertText = (
    <>
      <ErrorIcon fontSize="small" />
      &nbsp;See Alerts
    </>
  );

  React.useEffect(() => {
    // Remove the initial resetState call since we only want it on unmount
    if (
      localStorage.navigatedToAnalyticsFromSitesPage === "false" &&
      localStorage.filtersSavedClicked === "false"
    ) {
      fetchAllAnalyticsPageData(navigate);
    }

    if (
      localStorage.filtersSavedClicked === "true" &&
      localStorage.navigatedToAnalyticsFromSitesPage === "false"
    ) {
      fetchAllAnalyticsPageData(navigate);
      const savedAnalyticsFilters = JSON.parse(
        localStorage.savedAnalyticsFilters
      );
      if (savedAnalyticsFilters?.parent_group) {
        fetchSitesByParentGroup(savedAnalyticsFilters.parent_group, navigate);
      }
      if (savedAnalyticsFilters?.site) {
        fetchCamerasBySite(savedAnalyticsFilters.site, navigate);
      }
    }

    if (localStorage.navigatedToAnalyticsFromSitesPage === "true") {
      handleNavigatedFromSitesPage(navigate);
    }

    // Only run resetState when component unmounts
    return () => {
      resetState();
    };
  }, []);

  React.useEffect(() => {
    let newAlertURL = `/alerts?group=${userFilters.parent_group}`;
    if (userFilters.site.length === 1) {
      newAlertURL += `&site=${userFilters.site[0]}`;
    }
    if (userFilters.camera.length === 1) {
      newAlertURL += `&camera=${userFilters.camera[0]}`;
    }
    newAlertURL += `&start_date=${formattedDateGenerator(
      userFilters.start_date
    )}&end_date=${formattedDateGenerator(userFilters.end_date)}`;
    setAlertURL(newAlertURL);
  }, [userFilters]);

  const handleApplyFilters = () => {
    setFiltersApplied(true);
    setDownloadURL("");

    handleGetDownloadURL(navigate);

    // updateSearchParamValues(userFilters, searchParams, setSearchParams, "");

    // Update graph names based on filters
    if (!userFilters.parent_group) {
      setAlertsGraphName("Parent Group");
      setDateGraphName("");
    } else if (userFilters.parent_group && userFilters.site.length === 0) {
      setAlertsGraphName("Site");
      setDateGraphName("Parent Group");
    } else if (userFilters.site[0] && userFilters.camera.length === 0) {
      setAlertsGraphName("Camera");
      setDateGraphName("Site");
    }
    if (userFilters.camera[0]) {
      setAlertsGraphName("Camera");
      setDateGraphName("Camera");
    }

    // Fetch all data
    fetchAllGraphsData(navigate);
    setDateChangesMade(false);
  };

  return (
    <div>
      <AnalyticsAlerts />
      <div className="bg-actuate-grey absolute w-full h-full overflow-scroll">
        <div
          className={
            !navBarCollapsed
              ? "absolute left-[150px] right-[1px] h-full bg-white overflow-scroll"
              : "absolute left-[75px] right-[1px] h-full bg-white overflow-scroll"
          }
        >
          <div className="font-mulish absolute left-[20px] top-[20px]">
            <ParentGroupsDropDown
              navigate={navigate}
              navBarCollapsed={navBarCollapsed}
              searchParams={searchParams}
              setSearchParams={setSearchParams}
            />
          </div>
          <div
            className={
              !navBarCollapsed
                ? "font-mulish relative left-[233px] top-[20px] max-w-[800px] max-h-full"
                : "font-mulish relative left-[308px] top-[20px] max-w-[800px] max-h-full"
            }
          >
            <div className="relative top-0 left-0 w-[580px] columns-2">
              <div>
                <SiteDropDown
                  navigate={navigate}
                  searchParams={searchParams}
                  setSearchParams={setSearchParams}
                />
              </div>
              <div>
                <CameraDropDown
                  searchParams={searchParams}
                  setSearchParams={setSearchParams}
                />
              </div>
            </div>
          </div>
          <div
            className={
              !navBarCollapsed
                ? "font-mulish absolute left-[830px] top-[20px]"
                : "font-mulish absolute left-[905px] top-[20px]"
            }
          >
            <StartDateDropDown
              searchParams={searchParams}
              setSearchParams={setSearchParams}
            />
          </div>
          <div
            className={
              !navBarCollapsed
                ? "font-mulish absolute left-[990px] top-[20px]"
                : "font-mulish absolute left-[1065px] top-[20px]"
            }
          >
            <EndDateDropDown
              searchParams={searchParams}
              setSearchParams={setSearchParams}
            />
          </div>
          {userFilters.start_date && userFilters.end_date ? (
            <div
              className={
                !navBarCollapsed
                  ? "absolute top-[25px] left-[1150px]"
                  : "absolute top-[25px] left-[1225px]"
              }
            >
              <button
                className={
                  loadingData.allAlertsData ||
                  loadingData.falsePositiveData ||
                  loadingData.parentGroupsData ||
                  loadingData.percetageOfMotionData
                    ? "shadow-button bg-actuate-grey border-2	border-actuate-grey rounded font-mulish text-white text-base p-1.5 w-[120px]"
                    : "shadow-button bg-actuate-orange border-2	border-actuate-orange rounded font-mulish text-white text-base p-1.5 w-[120px] hover:bg-[#E67300]"
                }
                onClick={handleApplyFilters}
                disabled={
                  loadingData.allAlertsData ||
                  loadingData.falsePositiveData ||
                  loadingData.parentGroupsData ||
                  loadingData.percetageOfMotionData
                }
              >
                {loadingData.allAlertsData ||
                loadingData.falsePositiveData ||
                loadingData.parentGroupsData ||
                loadingData.percetageOfMotionData
                  ? `Loading...`
                  : ` Apply Filters`}
              </button>
            </div>
          ) : (
            <div
              className={
                !navBarCollapsed
                  ? "absolute top-[25px] left-[1150px]"
                  : "absolute top-[25px] left-[1225px]"
              }
            >
              <Tooltip title="Start and End Dates required" placement="bottom">
                <span>
                  <button
                    className="shadow-button bg-[#A9A9A9] rounded font-mulish text-white text-base p-1.5"
                    disabled={true}
                  >
                    Apply Filters
                  </button>
                </span>
              </Tooltip>
            </div>
          )}
          {filtersApplied ? (
            <div>
              {userFilters.parent_group && (
                <div
                  className={
                    !navBarCollapsed
                      ? "font-mulish absolute left-[980px] top-[95px]"
                      : "font-mulish absolute left-[1055px] top-[95px]"
                  }
                >
                  <ThemeProvider theme={theme}>
                    <Button
                      type="button"
                      color="primary"
                      onClick={() => navigate(alertURL)}
                      className="hover:bg-[#E67300]"
                    >
                      <Link
                        href={alertURL}
                        onClick={(e) => {
                          setNavigatedToAlertsFromAnalytics(true);
                          e.preventDefault();
                          navigate(alertURL);
                        }}
                        sx={{
                          textDecoration: "none",
                          color: "inherit",
                          cursor: "pointer",
                          height: "100%",
                        }}
                      >
                        {seeAlertText}
                      </Link>
                    </Button>
                  </ThemeProvider>
                </div>
              )}

              <div
                className={
                  !navBarCollapsed
                    ? "font-mulish absolute left-[1140px] top-[95px]"
                    : "font-mulish absolute left-[1215px] top-[95px]"
                }
              >
                <DownloadButton />
              </div>
              <div className="font-mulish relative left-[20px] top-[25px] w-[500px]">
                <Checkbox
                  checked={filtersSavedClicked}
                  onChange={(event: React.SyntheticEvent, result: boolean) => {
                    handleSetDefaultAnalyticsFilters(result);
                  }}
                />
                &nbsp; Set these filters as default configuration.
              </div>
              <div className="relative">
                <SVGLine navBarCollapsed={navBarCollapsed} />
                <AlertsByDateGraph navBarCollapsed={navBarCollapsed} />
                <div className="font-mulish absolute top-[40px] left-[20px] font-bold">
                  {`Total Alerts for selected dates: ${totalAlerts}`}
                </div>
                <AlertsByParentGroupsGraph
                  navigate={navigate}
                  navBarCollapsed={navBarCollapsed}
                />
                <FalsePositiveReductionGraphAnalytics
                  navBarCollapsed={navBarCollapsed}
                />
                <PercentageofMotionFilteredGraph
                  navBarCollapsed={navBarCollapsed}
                />
              </div>
            </div>
          ) : (
            <div className="relative top-[200px] left-[500px]">
              <p className="font-mulish text-[22px]">
                Please Apply Filters to view data.
              </p>
            </div>
          )}
        </div>
        <div className="justify-start">
          <NavBar
            navBarCollapsed={navBarCollapsed}
            setNavBarCollapsed={setNavBarCollapsed}
            props={{}}
          />
        </div>
      </div>
    </div>
  );
};
